import Layout from '@/components/Layout';
import createEmotionCache from '@/createEmotionCache';
import '@/styles/print.css';
import { EmotionCache } from '@emotion/react';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@verifime/design-tokens/dist/default/tokens.css';
import '@verifime/design-tokens/dist/clarity/tokens.css';
import { ToggleTheme } from '@verifime/styles';
import '@verifime/styles/styles/globals.css';
import '@verifime/styles/styles/quill.css';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  MITIGATION_TEMPLATE_PATH,
  NAV_ITEM_ASSESSMENTS,
  NAV_ITEM_CONFIGURATION,
  NAV_ITEM_CUSTOMERS,
  NAV_ITEM_HOME,
  RULE_PATH,
} from '../path';

import { Passwordless, PasswordlessContextProvider } from '@verifime/cognito-passwordless-auth';
import {
  ConfirmProvider,
  ErrorNotification,
  FavIcons,
  GlobalStyleOverride,
  SuccessNotification,
} from '@verifime/components';
import { isClient, useFeatureToggle } from '@verifime/utils';
import { SnackbarProvider } from 'notistack';
import AuthenticatedContainer from '../components/AuthenticatedContainer';
import PortalErrorBoundary from '@/components/PortalErrorBoundary';
import { SWRProvider } from '../services/hooks';
import '../services/index';
import { APPLICATION_NAME } from '../utils/constants';
import { LANDING_DESCRIPTION } from '../utils/messages';

if (isClient) {
  Passwordless.configure({
    cognitoIdpEndpoint: process.env.NEXT_PUBLIC_COGNITO_IDP_ENDPOINT,
    clientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
    userPoolId: process.env.NEXT_PUBLIC_COGNITO_USERPOOL_ID,
    storage: window.localStorage,
    fido2: {
      baseUrl: process.env.NEXT_PUBLIC_FIDO2_BASE_URL,
      authenticatorSelection: {
        userVerification: 'required',
      },
      rp: {
        id: process.env.NEXT_PUBLIC_FIDO2_RELYING_PARTY_ID, // default is not set and fallback to hostname e.g. localhost
      },
    },
    totp: {
      baseUrl: process.env.NEXT_PUBLIC_TOTP_BASE_URL,
      issuer: process.env.NEXT_PUBLIC_TOTP_ISSUER ?? 'VerifiMeDev',
    },
    debug: console.debug,
  });
}

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

// 404 and 500 must render for anyone: a bad link or a crash can land an
// unauthenticated visitor here, and they should see the branded page rather
// than be bounced to sign-in.
const UNAUTHENTICATED_PATHS = ['/auth/callback', '/404', '/500'];

const PATH_WITH_LAYOUT: string[] = [
  NAV_ITEM_HOME.path,
  NAV_ITEM_ASSESSMENTS.path,
  NAV_ITEM_CUSTOMERS.path,
  NAV_ITEM_CONFIGURATION.path,
  MITIGATION_TEMPLATE_PATH,
  RULE_PATH,
  `${NAV_ITEM_CUSTOMERS.path}/[customerId]`, // customer details page
];

type TAppProps = {
  emotionCache: EmotionCache;
} & AppProps;

const App = ({ Component, emotionCache = clientSideEmotionCache, pageProps }: TAppProps) => {
  const router = useRouter();
  // Read at the app layer, not inside the package: PasswordlessContextProvider
  // must not read environment variables itself (see task-3-brief.md).
  const { isInertMagicLinkEnabled } = useFeatureToggle();

  return (
    <PortalErrorBoundary>
      <SWRProvider>
        <QueryClientProvider client={queryClient}>
          <AppCacheProvider emotionCache={emotionCache}>
            <Head>
              <title>{APPLICATION_NAME}</title>
              <meta name="description" content={LANDING_DESCRIPTION} />
              <meta name="viewport" content="width=device-width, initial-scale=1" />
              <FavIcons />
            </Head>

            <ToggleTheme>
              <GlobalStyleOverride />
              <SnackbarProvider
                Components={{
                  successNotification: SuccessNotification,
                  errorNotification: ErrorNotification,
                }}
              >
                <ConfirmProvider>
                  <PasswordlessContextProvider
                    enableLocalUserCache={true}
                    deferConsumption={isInertMagicLinkEnabled}
                  >
                    {UNAUTHENTICATED_PATHS.includes(router.pathname) ? (
                      <Component {...pageProps} />
                    ) : (
                      <AuthenticatedContainer>
                        {PATH_WITH_LAYOUT.includes(router.pathname) ? (
                          <Layout>
                            <Component {...pageProps} />
                          </Layout>
                        ) : (
                          <Component {...pageProps} />
                        )}
                      </AuthenticatedContainer>
                    )}
                  </PasswordlessContextProvider>
                </ConfirmProvider>
              </SnackbarProvider>
            </ToggleTheme>
          </AppCacheProvider>
        </QueryClientProvider>
      </SWRProvider>
    </PortalErrorBoundary>
  );
};

export default App;
